import React, { useCallback, useMemo, useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAds } from 'components/context/AdsContext';
import { useAdsModal } from '../../components/context/ModalsContext';
import { AuthContext } from '../../components/context/AuthContext';
import { useCanCreateCampaign } from 'hooks/useCanCreateCampaign';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import chatsApi from '../../api/chat';
import logApi from '../../api/log';
import Step1PlatformSelection from './Step1PlatformSelection';
import Step2CampaignType from './Step2CampaignType';
import Step3AdPreview from './Step3AdPreview';
import Step4CampaignConfig from './Step4CampaignConfig';
import MetaAssetsSelectionStep from './MetaAssetsSelectionStep';
import MetaPermissionRequestModal from '../../components/modals/MetaPermissionRequestModal';
import { ALL_CAMPAIGN_TYPES } from './campaignTypes';
import SuccessAnimation from '../../components/SuccessAnimation';
import metaApi from '../../api/meta';

const { createLog } = logApi;

const CampaignWizard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasGoogleAdsConnected, hasMetaConnected } = useContext(AuthContext);
  const { activeGoogleAccount, activeMetaAccount } = useAds();
  const { openPlansModal, setPlansModalMessage, openUpdateBusinessDataModal } = useAdsModal();
  const { canCreateCampaign } = useCanCreateCampaign();

  const [step, setStep] = useState(1);
  const [platform, setPlatform] = useState(null); // 'google' | 'meta' | 'whatsapp' | 'all'
  const [selectedCampaignType, setSelectedCampaignType] = useState(null);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isMetaPlatformTransitioning, setIsMetaPlatformTransitioning] = useState(false);

  // Chat session and campaign state
  const [chatSession, setChatSession] = useState(null);
  const [currentCampaign, setCurrentCampaign] = useState(null);
  const [isCreatingCampaign, setIsCreatingCampaign] = useState(false);
  const [campaignError, setCampaignError] = useState(null);
  const [isPublishingCampaign, setIsPublishingCampaign] = useState(false);
  
  // Campaign editing state
  const [isEditingCampaign, setIsEditingCampaign] = useState(false);
  const [editError, setEditError] = useState(null);
  const [, setLastSavedMetaState] = useState(null);
  
  // Google Performance Max validation state
  const [isGooglePmaxValid, setIsGooglePmaxValid] = useState(false);
  
  // Google Search validation state
  const [isGoogleSearchValid, setIsGoogleSearchValid] = useState(true);
  
  // Step 4 unsaved changes state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({});
  
  // URL and session management
  const [showBackToStep2Warning, setShowBackToStep2Warning] = useState(false);
  
  // Success animation state
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [successCampaignData, setSuccessCampaignData] = useState(null);

  // Meta permissions and asset selection state
  const [showMetaPermissionModal, setShowMetaPermissionModal] = useState(false);

  useEffect(() => {
    if (!canCreateCampaign) {
      openUpdateBusinessDataModal();
    }
  }, [canCreateCampaign, openUpdateBusinessDataModal]);

  /**
   * Updates the URL with the current session ID
   */
  const updateURLWithSession = useCallback((sessionId) => {
    if (sessionId) {
      const newPath = `/campaign-wizard?session=${sessionId}`;
      navigate(newPath, { replace: true });
    }
  }, [navigate]);

  /**
   * Gets session ID from URL parameters
   */
  const getSessionFromURL = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get('session');
  }, [location.search]);

  /**
   * Gets step from URL parameters
   */
  const getStepFromURL = useCallback(() => {
    const params = new URLSearchParams(location.search);
    const stepParam = params.get('step');
    if (!stepParam) return null;
    const parsedStep = Number(stepParam);
    return Number.isFinite(parsedStep) ? parsedStep : null;
  }, [location.search]);

  /**
   * Gets platform from URL parameters
   */
  const getPlatformFromURL = useCallback(() => {
    const params = new URLSearchParams(location.search);
    return params.get('platform');
  }, [location.search]);

  /**
   * Handles going back to step 2 with warning
   */
  const handleBackToStep2 = () => {
    if (chatSession && currentCampaign) {
      setShowBackToStep2Warning(true);
    } else {
      setStep(2);
    }
  };

  /**
   * Confirms going back to step 2 (will create new campaign)
   */
  const confirmBackToStep2 = () => {
    setShowBackToStep2Warning(false);
    setStep(2);
    setSelectedCampaignType(null);
    setChatSession(null);
    setCurrentCampaign(null);
    // Clear URL session parameter
    navigate('/campaign-wizard', { replace: true });
  };

  /**
   * Cancels going back to step 2
   */
  const cancelBackToStep2 = () => {
    setShowBackToStep2Warning(false);
  };

  /**
   * Loads an existing campaign from session ID
   */
  const loadCampaignFromSession = useCallback(async (sessionId) => {
    try {
      setIsLoadingSession(true);
      setChatSession(sessionId);
      
      // Go directly to step 3 while loading
      setStep(3);
      
      // Get the campaign data
      const campaignResponse = await chatsApi.getCampaign(sessionId);
      
      if (campaignResponse.data) {
        setCurrentCampaign(campaignResponse.data);
        
        // Determine platform and campaign type from the loaded campaign
        const campaign = campaignResponse.data;
        if (campaign.provider) {
          setPlatform(campaign.provider);
        }
        if (campaign.campaign_type) {
          // Find the campaign type definition from ALL_CAMPAIGN_TYPES
          const campaignTypeDef = Object.values(ALL_CAMPAIGN_TYPES).find(type => 
            type.campaign_type === campaign.campaign_type
          );
          if (campaignTypeDef) {
            setSelectedCampaignType(campaignTypeDef);
          }
        }
      } else {
        console.error('No campaign data found for session:', sessionId);
        // Clear invalid session from URL and go back to step 1
        navigate('/campaign-wizard', { replace: true });
        setStep(1);
      }
    } catch (error) {
      console.error('Error loading campaign from session:', error);
      // Clear invalid session from URL and go back to step 1
      navigate('/campaign-wizard', { replace: true });
      setStep(1);
    } finally {
      setIsLoadingSession(false);
    }
  }, [navigate]);

  // Meta ad editing state
  const [metaState, setMetaState] = useState({
    title: 'Título ejemplo',
    description: 'Descripción ejemplo',
    images: [],
    imageIndex: 0,
    editingHeadline: -1,
    editingDescription: -1,
    headlineDraft: '',
    descriptionDraft: '',
    fileInputRef: useRef(null)
  });

  // Configuración de campaña (paso 4)
  const formatDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const platformLabel = useMemo(() => {
    switch (platform) {
      case 'google': return 'Google';
      case 'meta': return 'Meta';
      case 'whatsapp': return 'WhatsApp';
      default: return 'General';
    }
  }, [platform]);

  const defaultCurrency = useMemo(() => {
    if (platform === 'google') return activeGoogleAccount?.currency_code || activeGoogleAccount?.currency || 'USD';
    if (platform === 'meta') return activeMetaAccount?.currency || 'USD';
    return 'USD';
  }, [platform, activeGoogleAccount, activeMetaAccount]);

  const [config, setConfig] = useState({
    name: null,
    budget: null,
    currency: null,
    startDate: null,
    endDate: null,
    locations: null
  });

  // Inicializar defaults cuando tengamos plataforma/tipo
  useEffect(() => {
    if (selectedCampaignType) {
      setConfig((prev) => ({
        ...prev,
        name: `${platformLabel}-${selectedCampaignType.label}-${formatDate(new Date()).replaceAll('-', '')}`,
        budget: null,
        currency: null,
        startDate: null,
        endDate: null,
        locations: null
      }));
    }
  }, [platformLabel, selectedCampaignType, defaultCurrency]);

  // Initialize step and platform from URL on component mount
  useEffect(() => {
    const stepFromURL = getStepFromURL();
    const platformFromURL = getPlatformFromURL();
    
    if (stepFromURL && [1, 1.5, 2, 3, 4].includes(stepFromURL)) {
      setStep(stepFromURL);
    }
    
    if (platformFromURL && ['google', 'meta', 'whatsapp'].includes(platformFromURL)) {
      setPlatform(platformFromURL);
    }
  }, [getStepFromURL, getPlatformFromURL]);

  // Load session from URL on component mount
  useEffect(() => {
    const sessionFromURL = getSessionFromURL();
    if (sessionFromURL && !chatSession) {
      // Load existing campaign from session
      loadCampaignFromSession(sessionFromURL);
    }
  }, [chatSession, getSessionFromURL, loadCampaignFromSession]);

  // Initialize saved states when campaign is loaded (intentionally only when currentCampaign changes)
  useEffect(() => {
    if (currentCampaign) {
      // Initialize meta state with campaign data
      const adContent = currentCampaign.ad_content || {};
      const initialMetaState = {
        title: adContent.headlines?.[0] || 'Título ejemplo',
        description: adContent.descriptions?.[0] || 'Descripción ejemplo',
        images: adContent.media?.images || [],
        imageIndex: 0,
        editingHeadline: -1,
        editingDescription: -1,
        headlineDraft: '',
        descriptionDraft: '',
        fileInputRef: metaState.fileInputRef
      };
      
      setMetaState(initialMetaState);
      setLastSavedMetaState(initialMetaState);
      
      // Initialize config with campaign data
      const initialConfig = {
        name: currentCampaign.name || config.name,
        budget: currentCampaign.budget || config.budget,
        currency: currentCampaign.currency || config.currency,
        startDate: currentCampaign.start_date || config.startDate,
        endDate: currentCampaign.end_date || config.endDate,
        locations: currentCampaign.locations || config.locations
      };
      
      setConfig(initialConfig);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when campaign loads, not on config/metaState edits
  }, [currentCampaign]);

  // Update URL when session is created
  useEffect(() => {
    if (chatSession) {
      updateURLWithSession(chatSession);
    }
  }, [chatSession, updateURLWithSession]);

  const isConfigValid = useMemo(() => {
    if (!currentCampaign) return false;
    const nameOk = Boolean(currentCampaign?.name?.trim());
    const budgetOk = Number(currentCampaign.budget_amount_micros) >= 1000000;
    const datesOk = new Date(currentCampaign.start_date) <= new Date(currentCampaign.end_date);
    const locOk = Array.isArray(currentCampaign.targeting?.locations) && currentCampaign.targeting.locations.length > 0;
    return nameOk && budgetOk && datesOk && locOk;
  }, [currentCampaign]);

  // Check if step 3 is valid (has required media for campaigns)
  const isStep3Valid = useMemo(() => {
    if (step !== 3) return true;
    
    // For Meta campaigns, require at least one image
    if (selectedCampaignType?.campaign_type === 'META_TRAFFIC' || selectedCampaignType?.campaign_type === 'META_CONVERSIONS' || selectedCampaignType?.campaign_type === 'META_CATALOG') {
      const hasImages = (metaState.images && metaState.images.length > 0) || 
                       (currentCampaign?.ad_content?.media?.images && currentCampaign.ad_content.media.images.length > 0);
      return hasImages;
    }
    
    // For Google Performance Max campaigns, use the validation from GooglePreview component
    if (selectedCampaignType?.campaign_type === 'PERFORMANCE_MAX') {
      return isGooglePmaxValid;
    }
    
    // For Google Search campaigns, check text validation
    if (selectedCampaignType?.campaign_type === 'SEARCH') {
      return isGoogleSearchValid;
    }
    
    // For other campaign types, step 3 is always valid
    return true;
  }, [step, selectedCampaignType, metaState.images, currentCampaign, isGooglePmaxValid, isGoogleSearchValid]);

  // Handler functions for step components
  const handlePlatformSelect = async (selectedPlatform) => {
    setPlatform(selectedPlatform);

    // For Meta/WhatsApp, check permissions before proceeding to asset selection.
    if (selectedPlatform === 'meta' || selectedPlatform === 'whatsapp') {
      setIsMetaPlatformTransitioning(true);
      try {
        const { data } = await metaApi.checkMetaPermissions();

        if (!data.has_extended) {
          // Show permission modal
          setShowMetaPermissionModal(true);
        } else {
          // Go directly to asset selection step
          setStep(1.5);
        }
      } catch (err) {
        console.error('Error checking Meta permissions:', err);
        toast.error('Error al verificar permisos de Meta');
      } finally {
        setIsMetaPlatformTransitioning(false);
      }
    } else {
      // Other platforms skip to step 2
      setStep(2);
    }
  };

  const handleMetaPermissionModalClose = () => {
    setShowMetaPermissionModal(false);
    setPlatform(null);
    setStep(1);
  };

  const handleMetaAssetsComplete = () => {
    setStep(2);
  };

  const handleMetaAssetsBack = () => {
    setStep(1);
  };

  const handleMetaAssetsSkip = () => {
    setStep(2);
  };

  // Handler for Google Performance Max validation
  const handleGooglePmaxValidation = (isValid) => {
    setIsGooglePmaxValid(isValid);
  };

  // Handler for Google Search validation
  const handleGoogleSearchValidation = (isValid) => {
    setIsGoogleSearchValid(isValid);
  };

  // Handler for saving pending changes from Step 4
  const handleSaveChanges = async () => {
    if (Object.keys(pendingChanges).length === 0) return;
    
    console.log('handleSaveChanges called with pendingChanges:', pendingChanges);
    try {
      await handleCampaignUpdate(pendingChanges);
      setPendingChanges({});
      setHasUnsavedChanges(false);
      console.log('Changes saved successfully before publishing');
    } catch (error) {
      console.error('Error saving changes before publishing:', error);
      throw error; // Re-throw to be handled by the calling function
    }
  };

  const handleCampaignTypeSelect = async (selectedCampaignTypeObject) => {
    setSelectedCampaignType(selectedCampaignTypeObject);
    setCampaignError(null);
    
    // Reset Google Performance Max validation state
    setIsGooglePmaxValid(false);
    
    // Reset Google Search validation state
    setIsGoogleSearchValid(true);
    
    // Create campaign via API. Object containing campaign type data.
    await createCampaignViaAPI(selectedCampaignTypeObject);
  };

  /**
   * Edits the campaign using chatApi.editCampaign
   * @param {Object} payload - The campaign data to update
   */
  const editCampaign = async (payload) => {
    if (!chatSession) {
      console.error('No chat session available for editing');
      return;
    }

    try {
      setIsEditingCampaign(true);
      setEditError(null);
      
      const response = await chatsApi.editCampaign(chatSession, payload);
      
      if (response.data) {
        console.log('Campaign edited successfully:', response.data);
        console.log('Previous currentCampaign:', currentCampaign);
        
        // Extract the actual campaign data from the response
        const campaignData = response.data.v2_campaign || response.data;
        console.log('Extracted campaign data:', campaignData);
        
        // Update the current campaign with the new data
        setCurrentCampaign(prev => {
          const updated = { ...prev, ...campaignData };
          console.log('Updated currentCampaign:', updated);
          return updated;
        });
        return campaignData;
      } else {
        throw new Error('Error al editar la campaña');
      }
    } catch (error) {
      console.error('Error editing campaign:', error);
      setEditError(error.message || 'Error al editar la campaña');
      throw error;
    } finally {
      setIsEditingCampaign(false);
    }
  };

  /**
   * Global campaign update handler - works for any platform (Google, Meta, WhatsApp)
   */
  const handleCampaignUpdate = async (updates) => {
    console.log('handleCampaignUpdate called with:', updates);
    
    if (!currentCampaign || !chatSession) {
      console.log('No campaign or session, skipping edit:', { currentCampaign: !!currentCampaign, chatSession: !!chatSession });
      return;
    }

    try {
      // Build the edit payload dynamically based on what's being updated
      const editPayload = {};
      
      // Handle ad_content updates
      if (updates.title || updates.titles || updates.description || updates.descriptions || updates.url || updates.images || updates.ad_content) {
        editPayload.ad_content = { ...currentCampaign.ad_content };
        
        if (updates.title || updates.titles) {
          editPayload.ad_content.headlines = updates.titles || [updates.title];
        }
        
        if (updates.description || updates.descriptions) {
          editPayload.ad_content.descriptions = updates.descriptions || [updates.description];
        }
        
        if (updates.url) {
          editPayload.ad_content.final_url = updates.url;
        }
        
        if (updates.images) {
          editPayload.ad_content.media = {
            ...editPayload.ad_content.media,
            images: updates.images
          };
        }
        
        // Handle direct ad_content updates (from Step 4)
        if (updates.ad_content) {
          editPayload.ad_content = {
            ...editPayload.ad_content,
            ...updates.ad_content
          };
        }
      }
      
      // Handle Google-specific settings
      if (updates.keywords) {
        editPayload.google_settings = {
          ...currentCampaign.google_settings,
          keywords: updates.keywords
        };
      }
      
      // Handle Meta-specific settings
      if (updates.meta_settings) {
        editPayload.meta_settings = {
          ...currentCampaign.meta_settings,
          ...updates.meta_settings
        };
      }
      
      // Handle WhatsApp-specific settings
      if (updates.whatsapp_settings) {
        editPayload.whatsapp_settings = {
          ...currentCampaign.whatsapp_settings,
          ...updates.whatsapp_settings
        };
      }
      
      // Handle general campaign settings
      if (updates.name) editPayload.name = updates.name;
      if (updates.budget) editPayload.budget = updates.budget;
      if (updates.budget_amount_micros) editPayload.budget_amount_micros = updates.budget_amount_micros;
      if (updates.currency) editPayload.currency = updates.currency;
      if (updates.start_date) editPayload.start_date = updates.start_date;
      if (updates.end_date) editPayload.end_date = updates.end_date;
      if (updates.locations) editPayload.locations = updates.locations;
      
      // Handle targeting settings
      if (updates.targeting) {
        editPayload.targeting = {
          ...currentCampaign.targeting,
          ...updates.targeting
        };
      }

      if (Object.keys(editPayload).length > 0) {
        console.log('Calling editCampaign with payload:', editPayload);
        const updatedCampaign = await editCampaign(editPayload);
        console.log('Campaign edit completed successfully, updated campaign:', updatedCampaign);
        return updatedCampaign;
      } else {
        console.log('No changes to apply');
        return currentCampaign;
      }
    } catch (error) {
      console.error('Error updating campaign:', error);
    }
  };

  /**
   * Legacy handler for Meta state changes (for backward compatibility)
   */
  const handleMetaStateChangeWithEdit = async (updates) => {
    const newMetaState = { ...metaState, ...updates };
    setMetaState(newMetaState);

    // Convert Meta state updates to global campaign updates
    const campaignUpdates = {};
    
    if (updates.title) {
      campaignUpdates.titles = [updates.title];
    }
    if (updates.description) {
      campaignUpdates.descriptions = [updates.description];
    }
    if (updates.images) {
      campaignUpdates.images = updates.images;
    }
    
    if (Object.keys(campaignUpdates).length > 0) {
      console.log('Sending campaign updates:', campaignUpdates);
      await handleCampaignUpdate(campaignUpdates);
    }
  };

  /**
   * Publishes the campaign using chatApi.sendCampaign
   */
  const handlePublishCampaign = async () => {
    if (!chatSession) {
      console.error('No chat session available for publishing');
      return;
    }

    try {
      setIsPublishingCampaign(true);
      
      // Validate connections before publishing (same logic as AiCampaignCreation)
      if (currentCampaign?.provider === 'google' && !hasGoogleAdsConnected) {
        createLog({
          level: 'info',
          message: `Click en "Publicar" campaña en Google (${currentCampaign?.campaign_type}) pero no se pudo publicar porque no se tiene conectado Google Ads`,
          channel: "notificaciones-campañas"
        });
        toast.error('Necesitás conectar tu cuenta de Google Ads para publicar esta campaña');
        return;
      }

      if (currentCampaign?.provider === 'meta' && !hasMetaConnected) {
        createLog({
          level: 'info',
          message: `Click en "Publicar" campaña en Meta (${currentCampaign?.campaign_type}) pero no se pudo publicar porque no se tiene conectado Meta Ads`,
          channel: "notificaciones-campañas"
        });
        toast.error('Necesitás conectar tu cuenta de Meta Ads para publicar esta campaña');
        return;
      }

      // Save any pending changes from Step 4 before publishing
      if (step === 4 && hasUnsavedChanges) {
        console.log('Saving pending changes before publishing...');
        await handleSaveChanges();
      }
      
      const response = await chatsApi.sendCampaign(chatSession);
      
      if (response.data) {
        console.log('Campaign published successfully:', response.data);
        
        // Success handling with animation instead of toast
        const providerId = response?.data?.v2_campaign?.provider_campaign_id;
        
        // Set success animation data
        setSuccessCampaignData({
          provider: currentCampaign.provider,
          providerId: providerId
        });
        
        // Show success animation
        setShowSuccessAnimation(true);
      } else {
        throw new Error('Error al publicar la campaña');
      }
    } catch (error) {
      console.error('Error publishing campaign:', error);
      
      // Handle MAX_CAMPAIGNS_REACHED error
      if (error?.response?.data?.error_status === "MAX_CAMPAIGNS_REACHED") {
        createLog({
          level: 'info',
          message: `Click en "Publicar" campaña de ${currentCampaign?.provider} (${currentCampaign?.campaign_type}) pero no se pudo publicar porque se alcanzó el límite de campañas para el plan actual`,
          channel: "notificaciones-campañas"
        });
        setPlansModalMessage(error.response.data.error);
        openPlansModal();
        return;
      }
      
      // Handle other errors with toast
      toast.error(`Error al crear la campaña. ${error.response?.data?.error || 'Por favor, intenta nuevamente.'}`);
    } finally {
      setIsPublishingCampaign(false);
    }
  };

  /**
   * Handles the completion of success animation
   */
  const handleSuccessAnimationComplete = () => {
    setShowSuccessAnimation(false);
    setSuccessCampaignData(null);
  };

  /**
   * Creates a campaign via API using chat session
   * @param {string} selectedCampaignType - The selected campaign type
   */
  const createCampaignViaAPI = async (selectedCampaignTypeObject) => {
    try {
      setIsCreatingCampaign(true);
      setCampaignError(null);

      console.log({ selectedCampaignTypeObject });

      // Step 1: Create chat session
      const sessionResponse = await chatsApi.createChatSession({
        name: `Campaña ${selectedCampaignTypeObject.label}`
      });

      const sessionId = sessionResponse?.data?.id;
      if (!sessionId) {
        throw new Error('Error al crear la sesión de chat');
      }
      setChatSession(sessionId);


      let message = `Crear campaña de tipo ${selectedCampaignTypeObject.campaign_type} para plataforma ${selectedCampaignTypeObject.platform}. `;
      if (selectedCampaignTypeObject.chatbotMessage) {
        message += ` ${selectedCampaignTypeObject.chatbotMessage}`;
      }
      // Step 2: Post campaign conversation message
      const messageResponse = await chatsApi.postCampaignConversationMessage(sessionId, {
        message
      });

      if (!messageResponse?.data?.message) {
        throw new Error('Error al enviar el mensaje de creación de campaña');
      }

      // Step 3: Get the created campaign
      const campaignResponse = await chatsApi.getCampaign(sessionId);
      
      if (!campaignResponse.data) {
        throw new Error('Error al obtener la campaña creada');
      }

      setCurrentCampaign(campaignResponse.data);
      setStep(3); // Move to step 3 after successful creation

    } catch (error) {
      console.error('Error creating campaign:', error);
      setCampaignError(error.message || 'Error al crear la campaña');
      // Don't move to step 3 if there's an error
    } finally {
      setIsCreatingCampaign(false);
    }
  };

  // Show loading screen when loading session
  if (isLoadingSession) {
    return (
      <div className="min-h-screen lg:px-28 pt-10 pb-8 lg:pt-16 flex items-center justify-center">
        <div className="max-w-5xl mx-auto text-center">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold text-white text-center">Cargando campaña</h1>
            <p className="text-sm text-white/80 text-center">Recuperando tu campaña desde la sesión...</p>
          </div>
          
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-white/80">Cargando datos de la campaña...</p>
              <p className="text-xs text-white/60 mt-2">Session: {getSessionFromURL()}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`min-h-screen px-3 lg:px-28 pt-3 lg:pt-4 ${step >= 2 && step !== 2 ? 'pb-32' : step === 2 ? 'pb-24 md:pb-28' : 'pb-32'} lg:pt-6`}>
      {/* Modern Floating Header — margen extra bajo FloatingBrandSelector (fixed top-4 ~3.5rem) */}
      <div className="max-w-6xl mx-auto mb-4 lg:mb-6 mt-10 sm:mt-12 lg:mt-14">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl lg:rounded-2xl shadow-xl px-3 pb-3 pt-5 sm:pt-6 lg:px-5 lg:pb-4 lg:pt-7 relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent"></div>
          <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-10 translate-x-10"></div>
          <div className="absolute bottom-0 left-0 w-16 h-16 bg-white/5 rounded-full translate-y-8 -translate-x-8"></div>
          
          {/* Content */}
          <div className="relative z-10">
            {/* Title */}
            <div className="text-center mb-3 lg:mb-4">
              <h1 className="text-lg lg:text-xl font-bold text-white">¡Creá tu campaña!</h1>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center justify-center space-x-1 sm:space-x-2 lg:space-x-3">
              {/* Step 1 - Plataforma */}
              <div className="flex items-center">
                <div className={`w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                  step >= 1 
                    ? 'bg-white text-blue-600 shadow-lg' 
                    : 'bg-white/20 text-white/60'
                }`}>
                  {step > 1 ? '✓' : '1'}
                </div>
                <span className={`ml-0.5 sm:ml-1 lg:ml-1.5 text-xs font-medium transition-colors duration-300 hidden sm:inline ${
                  step >= 1 ? 'text-white' : 'text-white/60'
                }`}>
                  Plataforma
                </span>
              </div>

              {/* Connector */}
              <div className={`w-2 sm:w-4 lg:w-6 h-0.5 transition-colors duration-300 ${
                step >= 2 ? 'bg-white' : 'bg-white/30'
              }`}></div>

              {/* Step 2 - Tipo */}
              <div className="flex items-center">
                <div className={`w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                  step >= 2 
                    ? 'bg-white text-blue-600 shadow-lg' 
                    : 'bg-white/20 text-white/60'
                }`}>
                  {step > 2 ? '✓' : '2'}
                </div>
                <span className={`ml-0.5 sm:ml-1 lg:ml-1.5 text-xs font-medium transition-colors duration-300 hidden sm:inline ${
                  step >= 2 ? 'text-white' : 'text-white/60'
                }`}>
                  Tipo
                </span>
              </div>

              {/* Connector */}
              <div className={`w-2 sm:w-4 lg:w-6 h-0.5 transition-colors duration-300 ${
                step >= 3 ? 'bg-white' : 'bg-white/30'
              }`}></div>

              {/* Step 3 - Anuncio */}
              <div className="flex items-center">
                <div className={`w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                  step >= 3 
                    ? 'bg-white text-blue-600 shadow-lg' 
                    : 'bg-white/20 text-white/60'
                }`}>
                  {step > 3 ? '✓' : '3'}
                </div>
                <span className={`ml-0.5 sm:ml-1 lg:ml-1.5 text-xs font-medium transition-colors duration-300 hidden sm:inline ${
                  step >= 3 ? 'text-white' : 'text-white/60'
                }`}>
                  Anuncio
                </span>
              </div>

              {/* Connector */}
              <div className={`w-2 sm:w-4 lg:w-6 h-0.5 transition-colors duration-300 ${
                step >= 4 ? 'bg-white' : 'bg-white/30'
              }`}></div>

              {/* Step 4 - Campaña */}
              <div className="flex items-center">
                <div className={`w-5 h-5 sm:w-6 sm:h-6 lg:w-8 lg:h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                  step >= 4 
                    ? 'bg-white text-blue-600 shadow-lg' 
                    : 'bg-white/20 text-white/60'
                }`}>
                  {step > 4 ? '✓' : '4'}
                </div>
                <span className={`ml-0.5 sm:ml-1 lg:ml-1.5 text-xs font-medium transition-colors duration-300 hidden sm:inline ${
                  step >= 4 ? 'text-white' : 'text-white/60'
                }`}>
                  Campaña
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

        {/* Botón volver en la parte superior para pasos 2, 3 y 4 */}
        {step >= 2 && step !== 3 && step !== 4 && !isCreatingCampaign && (
          <div className="max-w-5xl mx-auto mb-3 px-3 lg:px-0">
            <button
              type="button"
              onClick={() => {
                if (step === 3) {
                  handleBackToStep2();
                } else {
                  setStep(Math.max(1, step - 1));
                }
              }}
              className="flex items-center gap-2 text-sm text-white/80 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>
                {step === 2 ? 'Volver a plataformas' : step === 3 ? 'Volver a tipos de campaña' : 'Volver al anuncio'}
              </span>
            </button>
          </div>
        )}

        <div className="max-w-5xl mx-auto space-y-4 lg:space-y-6 px-3 lg:px-0">
          {step === 1 && (
            <Step1PlatformSelection
              onPlatformSelect={handlePlatformSelect}
              isMetaTransitioning={isMetaPlatformTransitioning}
              transitioningPlatform={platform}
            />
          )}
          {step === 1.5 && (platform === 'meta' || platform === 'whatsapp') && (
            <MetaAssetsSelectionStep
              activeMetaAccount={activeMetaAccount}
              onComplete={handleMetaAssetsComplete}
              onBack={handleMetaAssetsBack}
              onSkip={handleMetaAssetsSkip}
              platform={platform}
            />
          )}
          {step === 2 && (
            <Step2CampaignType 
              platform={platform}
              selectedCampaignType={selectedCampaignType}
              onCampaignTypeSelect={handleCampaignTypeSelect}
              isCreatingCampaign={isCreatingCampaign}
              campaignError={campaignError}
            />
          )}
          {step === 3 && (
            <div>
              <Step3AdPreview 
                campaignTypeObject={selectedCampaignType}
                metaState={metaState}
                onMetaStateChange={handleMetaStateChangeWithEdit}
                onCampaignUpdate={handleCampaignUpdate}
                currentCampaign={currentCampaign}
                chatSession={chatSession}
                isEditingCampaign={isEditingCampaign}
                editError={editError}
                onValidationChange={selectedCampaignType?.campaign_type === 'PERFORMANCE_MAX' ? handleGooglePmaxValidation : handleGoogleSearchValidation}
              />
              
              {/* Google Performance Max validation error message */}
              {selectedCampaignType?.campaign_type === 'PERFORMANCE_MAX' && !isGooglePmaxValid && (
                <div className="mt-4 p-4 bg-red-900/50 border border-red-700/50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                      <span className="text-white text-xs font-bold">!</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-red-200">Medios requeridos</h4>
                      <p className="text-sm text-red-300/90 mt-1">
                        Para continuar con tu campaña Performance Max, necesitas subir:
                      </p>
                      <ul className="text-sm text-red-300/90 mt-2 ml-4 list-disc">
                        <li>Un logo de tu marca</li>
                        <li>Al menos una imagen o video para el anuncio</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
          {step === 4 && (
            <Step4CampaignConfig 
              platform={platform}
              campaignTypeObject={selectedCampaignType}
              onCampaignUpdate={handleCampaignUpdate}
              defaultCurrency={defaultCurrency}
              currentCampaign={currentCampaign}
              chatSession={chatSession}
              isEditingCampaign={isEditingCampaign}
              editError={editError}
              hasUnsavedChanges={hasUnsavedChanges}
              setHasUnsavedChanges={setHasUnsavedChanges}
              pendingChanges={pendingChanges}
              setPendingChanges={setPendingChanges}
            />
          )}
        </div>

        {step >= 2 && step !== 2 && (
          <div className="fixed left-0 right-0 lg:bottom-0 bottom-[92px] bg-slate-900/95 backdrop-blur border-t border-white/20 p-3 lg:p-4 z-[10001]">
            <div className="max-w-7xl mx-auto flex items-center justify-between px-3 lg:px-0">
              <button
                type="button"
                className="text-sm text-white/80 underline hover:text-white"
                onClick={() => {
                  if (step === 3) {
                    handleBackToStep2();
                  } else {
                    setStep(Math.max(1, step - 1));
                  }
                }}
              >
                Volver
              </button>
              <div className="flex items-center gap-2 lg:gap-3">
                {/* Save Changes Button - Only show on step 4 when there are unsaved changes */}
                {step === 4 && hasUnsavedChanges && (
                  <button
                    type="button"
                    onClick={async () => {
                      await handleSaveChanges();
                    }}
                    disabled={isEditingCampaign}
                    className="px-3 lg:px-4 py-2 border border-white/30 text-white/90 rounded-lg text-sm font-medium hover:bg-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEditingCampaign ? 'Guardando...' : 'Guardar'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    if (step === 2 && selectedCampaignType) setStep(3);
                    else if (step === 3 && isStep3Valid) setStep(4);
                    else if (step === 4 && isConfigValid) {
                      handlePublishCampaign();
                    }
                  }}
                  disabled={(step === 2 && !selectedCampaignType) || (step === 3 && !isStep3Valid) || (step === 4 && !isConfigValid) || isPublishingCampaign}
                  className={`px-3 lg:px-4 py-2 rounded-lg text-white text-sm flex items-center gap-2 ${
                    (step === 2 && !selectedCampaignType) || (step === 3 && !isStep3Valid) || (step === 4 && !isConfigValid) || isPublishingCampaign
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-primary hover:bg-primary'
                  }`}
                >
                  {isPublishingCampaign && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  {step === 4 ? (isPublishingCampaign ? 'Publicando...' : 'Publicar') : 'Continuar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Success Animation */}
        <SuccessAnimation
          isVisible={showSuccessAnimation}
          onComplete={handleSuccessAnimationComplete}
          campaignProvider={successCampaignData?.provider}
          providerId={successCampaignData?.providerId}
        />

        {/* Warning Modal for going back to step 2 */}
        {showBackToStep2Warning && (
          <div className="fixed inset-0 modal-safe-area bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-xl shadow-xl border border-white/20 w-full max-w-md p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-yellow-900/50 rounded-full flex items-center justify-center border border-yellow-700/50">
                  <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">¿Volver a seleccionar tipo de campaña?</h3>
                  <p className="text-sm text-white/80">Esto creará una nueva campaña y perderás los cambios actuales.</p>
                </div>
              </div>
              
              <div className="bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-3 mb-4">
                <p className="text-sm text-yellow-200">
                  <strong>Advertencia:</strong> Si continúas, se creará una nueva campaña y perderás todos los cambios realizados en la campaña actual.
                </p>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={cancelBackToStep2}
                  className="px-4 py-2 text-sm text-white/80 hover:text-white transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmBackToStep2}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Continuar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Meta Permission Request Modal */}
        <MetaPermissionRequestModal
          isOpen={showMetaPermissionModal}
          onClose={handleMetaPermissionModalClose}
          platform={platform}
        />
      </div>
    </>
  );
};

export default CampaignWizard;
